import { useEffect, useState } from 'react';
import { FormikState } from 'formik';
import { useWrappedFormik } from 'src/hooks/useWrappedFormik';

import { IModalLayoutProps, ModalLayout } from '../../Modals/ModalLayout';
import { InputText } from '@nebo-team/vobaza.ui.inputs.input-text/dist';
import { InputSelect, Variant } from '@nebo-team/vobaza.ui.inputs.input-select/dist';
import { Title } from '@nebo-team/vobaza.ui.title/dist';
import { Button } from '@nebo-team/vobaza.ui.button/dist';

import type { IError } from 'src/models/IError';
import type { IAdmin } from 'src/models/IAdmin';
import { adminEditValidationSchema, adminValidationSchema } from 'src/validationSchemas/Admin';

import { newRoles, newStatus, blockAdminReasons } from '../../../src/const/userLib';

import styles from './styles.module.scss';

interface Props {
  onSubmitForm(values: IAdmin): void;
  onDelete?(): void;
  isAdd?: boolean;
  initialData: any;
  backErrors?: IError[];
}

interface IForm extends Omit<IAdmin, 'status' | 'role' | 'block_reason'> {
  password_reset: string;
  block_reason?: null | Variant;
  status: null | Variant;
  role: null | Variant;
}

const initialValues = {
  name: '',
  surname: '',
  email: '',
  role: null,
  status: null,
  password: '',
} as IForm;

export const ModalEditUser = ({
  active,
  onClose,
  onSubmitForm,
  initialData = {},
  isAdd,
  onDelete,
  backErrors,
}: Props & IModalLayoutProps) => {
  const [generalErrors, setGeneralErrors] = useState<string[]>([]);
  const [isShow, setIsShow] = useState<boolean>(false);
  const [isShowRepeat, setIsShowRepeat] = useState<boolean>(false);
  const [isOpenChangePassword, setOpenChangePassword] = useState<boolean>(false);

  const handleSubmitForm = () => {
    const newAdmin = {
      name: values.name,
      surname: values.surname,
      email: values.email,
      role: values.role?.code,
      status: values.status?.code,
      password: values.password,
      block_reason: values.block_reason ? values.block_reason.code : undefined,
    } as IAdmin;

    onSubmitForm(newAdmin);
  };

  const {
    values,
    handleChange,
    handleChangeSelect,
    errors,
    handleSubmit,
    handleBlur,
    setFormikState,
    setErrors,
    resetForm,
    setValues,
  } = useWrappedFormik<IForm>({
    initialValues: {
      ...(initialValues as any),
      password_reset: '',
      block_reason: null,
    },
    validationSchema: isAdd || isOpenChangePassword ? adminValidationSchema : adminEditValidationSchema,
    validateOnChange: true,
    onSubmit: handleSubmitForm,
  });

  const handleButtonReset = () => {
    const normalizedInitialData = {
      ...initialData,
      role: newRoles.find((item) => item.code === initialData.role),
      status: newStatus.find((item) => item.code === initialData.status),
      block_reason: initialData.block_reason
        ? blockAdminReasons.find((item) => item.code === initialData.block_reason)
        : null,
    };
    setValues(normalizedInitialData);
    setErrors({});
  };

  useEffect(() => {
    setFormikState((state: FormikState<IForm>) => {
      return {
        ...state,
        errors: {},
        values: {
          ...state.values,
          ...initialData,
          ...{
            role: !isAdd
              ? newRoles.find((item) => item.code === initialData.role)
              : newRoles.find((item) => item.code === state?.values?.role?.code) || null,
            status: isAdd
              ? newStatus.find((item) => item.code === 'ACTIVE')
              : newStatus.find((item) => item.code === initialData.status),
            block_reason: initialData.block_reason
              ? blockAdminReasons.find((item) => item.code === initialData.block_reason)
              : null,
          },
        },
      };
    });
  }, [initialData, active]);

  useEffect(() => {
    return () => {
      if (!active) {
        resetForm();
        setGeneralErrors([]);
        setOpenChangePassword(false);
      }
    };
  }, [active]);

  useEffect(() => {
    if (backErrors) {
      const errs = backErrors.reduce((acc: object, cur: any) => {
        if (cur?.source) {
          return { ...acc, [cur.source]: cur.title };
        }

        if (cur.title) {
          setGeneralErrors((prevState) => [...prevState, cur.title]);
        }

        return acc;
      }, {});

      setErrors(errs);
    }
  }, [backErrors]);

  useEffect(() => {
    setGeneralErrors([]);
  }, [values]);

  return (
    <ModalLayout active={active} onClose={onClose}>
      <form onSubmit={handleSubmitForm}>
        <Title className={styles.modalTitle} element={'h2'}>
          {isAdd ? 'Добавление администратора' : 'Редактирование администратора'}
        </Title>

        <div className={styles.modalField}>
          <InputText
            label="Имя"
            name="name"
            value={values.name}
            onChange={handleChange}
            error={errors?.name}
            onBlur={handleBlur}
            required
          />
        </div>

        <div className={styles.modalField}>
          <InputText
            label="Фамилия"
            name="surname"
            value={values.surname}
            onChange={handleChange}
            error={errors?.surname}
            onBlur={handleBlur}
            required
          />
        </div>

        <div className={styles.modalField}>
          <InputText
            label="Email (логин)"
            name="email"
            value={values.email}
            onChange={handleChange}
            error={errors?.email}
            onBlur={handleBlur}
            required
          />
        </div>

        <div className={styles.modalField}>
          <InputSelect
            variants={newRoles}
            label="Роль"
            name="role"
            currentValue={values.role}
            onChange={(e) => handleChangeSelect('role', e)}
            error={errors?.role}
            required
          />
        </div>

        <div className={styles.modalField}>
          <InputSelect
            variants={newStatus}
            label="Статус"
            name="status"
            currentValue={values.status}
            onChange={(e) => handleChangeSelect('status', e)}
            error={errors?.status}
            required
            disabled={isAdd}
          />
        </div>
        {values?.status?.code === 'BLOCKED' && (
          <div className={styles.modalField}>
            <InputSelect
              variants={blockAdminReasons}
              label="Причина блокировки"
              name="block_reason"
              currentValue={values.block_reason || null}
              onChange={(e) => handleChangeSelect('block_reason', e)}
              error={errors?.block_reason}
            />
          </div>
        )}

        {!isAdd && (
          <>
            <div className={styles.modalField}>
              <InputText
                type="password"
                label={isAdd ? 'Придумайте пароль' : 'Пароль'}
                name={isAdd ? 'password' : 'passwordOld'}
                value={isAdd ? values.password || '' : 'mock-password'}
                disabled={!isAdd}
                onBlur={handleBlur}
                required
              />
            </div>
            {!isOpenChangePassword && (
              <Button
                text={'Изменить пароль'}
                variation={'dashed'}
                onClick={() => setOpenChangePassword(true)}
                className={styles.changePassword}
              />
            )}
          </>
        )}

        {(isAdd || isOpenChangePassword) && (
          <>
            <div className={styles.modalField}>
              <InputText
                notion={isAdd ? 'от 8 символов, только латинские буквы и цифры' : ''}
                type={isShow ? 'text' : 'password'}
                label={isAdd ? 'Придумайте пароль' : 'Новый пароль'}
                name="password"
                value={values.password || ''}
                onChange={handleChange}
                error={errors?.password}
                icon={isShow ? 'Eye' : 'CloseEye'}
                onClickIcon={() => {
                  setIsShow(!isShow);
                }}
                onBlur={handleBlur}
                required
              />
            </div>
            <div className={styles.modalField}>
              <InputText
                type={isShowRepeat ? 'text' : 'password'}
                label="Повторите пароль"
                name="password_reset"
                value={values.password_reset || ''}
                onChange={handleChange}
                error={errors?.password_reset}
                icon={isShowRepeat ? 'Eye' : 'CloseEye'}
                onClickIcon={() => {
                  setIsShowRepeat(!isShowRepeat);
                }}
                onBlur={handleBlur}
                required
              />
            </div>
          </>
        )}

        {!isAdd && (
          <div className={styles.modalFieldLong}>
            <Button text={'Удалить администратора'} variation={'ternary'} onClick={onDelete} color={'#E73030'} />
          </div>
        )}

        {generalErrors &&
          generalErrors.map((err) => (
            <p className={styles.modalErr} key={err}>
              {err}
            </p>
          ))}

        <Button
          text={isAdd ? 'Добавить' : 'Сохранить'}
          onClick={() => handleSubmit()}
          size={'big'}
          className={styles.modalSubmit}
        />

        {!isAdd && (
          <Button
            text={'Отменить изменения'}
            variation={'ternary'}
            onClick={handleButtonReset}
            className={styles.cancelChanges}
          />
        )}
      </form>
    </ModalLayout>
  );
};
